import { z } from "zod";

// Display position types (corresponding to FrontPosition in Python)
export const FrontPositionSchema = z.enum([
  "at_start",
  "in_conversation_tool_start",
  "in_conversation_tool_end",
  "at_end",
]);

// Frontend component types (corresponding to FrontComponentName in Python)
export const FrontComponentNameSchema = z
  .enum([
    "ErrorComponent",
    "CrashComponent",
    "InfoToolComponent",
    "TextReplaceToolComponent",
  ])
  .or(z.string());

// Initialization event
export const InitEventSchema = z.object({
  type: z.literal("init"),
  conversation_id: z.number(),
  parent_id: z.number().nullable().optional(),
});

// Text event (no specific type specified in Python class)
export const TextEventSchema = z.object({
  text: z.string(),
});

// End event
export const EndEventSchema = z.object({
  type: z.literal("end"),
  backend_id: z.number().nullable().optional(),
});

// Base tool event
export const ToolEventSchema = z.object({
  type: z.literal("tool"),
  tool_name: z.string(),
  tool_id: z.string(),
  front_component_name: FrontComponentNameSchema.nullable().optional(),
  front_position: FrontPositionSchema.nullable().optional(),
});

// Tool result event
export const ToolEventResultSchema = ToolEventSchema.extend({
  type: z.literal("tool_result"),
  parameters: z.record(z.string(), z.any()),
  front_component_data: z.record(z.string(), z.any()).nullable().optional(),
});

// Tool start event
export const ToolStartEventSchema = ToolEventSchema.extend({
  type: z.literal("tool_start"),
});

// Tool success event
export const ToolSuccessEventSchema = ToolEventResultSchema.extend({
  type: z.literal("tool_success"),
});

// Tool error event
export const ToolErrorEventSchema = ToolEventResultSchema.extend({
  type: z.literal("tool_error"),
  error: z.string(),
  front_component_name: FrontComponentNameSchema.default("ErrorComponent"),
  front_position: FrontPositionSchema.default("at_end"),
});

// Tool crash event
export const ToolCrashEventSchema = ToolEventResultSchema.extend({
  type: z.literal("tool_crash"),
  error: z.string(),
  front_component_name: FrontComponentNameSchema.default("CrashComponent"),
  front_position: FrontPositionSchema.default("at_end"),
});

// Text replace tool event
export const TextReplaceToolEventSchema = z.object({
  type: z.literal("text_replace_tool"),
  tool_name: z.string(),
  tool_id: z.string(),
  front_component_name: z.string().default("TextReplaceToolComponent"),
  front_position: FrontPositionSchema.default("in_conversation_tool_end"),
  parameters: z
    .object({
      position: z.number(),
      length: z.number(),
      original_text: z.string(),
    })
    .and(z.record(z.string(), z.any())),
  front_component_data: z.record(z.string(), z.any()).nullable().optional(),
});

// Schema for typed events
export const TypedEventSchema = z.discriminatedUnion("type", [
  InitEventSchema,
  EndEventSchema,
  ToolEventSchema,
  ToolStartEventSchema,
  ToolEventResultSchema,
  ToolSuccessEventSchema,
  ToolErrorEventSchema,
  ToolCrashEventSchema,
  TextReplaceToolEventSchema,
]);

// Global schema that accepts either a typed event or a TextEvent
export const StreamEventSchema = z.union([TypedEventSchema, TextEventSchema]);

// Inferred types from schemas
export type StreamEvent = z.infer<typeof StreamEventSchema>;
export type FrontPosition = z.infer<typeof FrontPositionSchema>;
export type FrontComponentName = z.infer<typeof FrontComponentNameSchema>;

export type ToolStartEvent = z.infer<typeof ToolStartEventSchema>;
export type ToolSuccessEvent = z.infer<typeof ToolSuccessEventSchema>;
export type ToolErrorEvent = z.infer<typeof ToolErrorEventSchema>;
export type ToolCrashEvent = z.infer<typeof ToolCrashEventSchema>;
export type TextReplaceToolEvent = z.infer<typeof TextReplaceToolEventSchema>;

export type ToolEvent =
  | ToolStartEvent
  | ToolSuccessEvent
  | ToolErrorEvent
  | ToolCrashEvent
  | TextReplaceToolEvent;

export type ToolEventEndEvent =
  | ToolSuccessEvent
  | ToolErrorEvent
  | ToolCrashEvent;

export type ToolProblemResultEvent = ToolErrorEvent | ToolCrashEvent;
